import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
const PARTNER_SOURCES = new Set([
  "channel3", "rakuten", "cj", "awin", "flexoffers", "amazon_associates",
  "ebay", "walmart_affiliate", "skimlinks", "target", "bestbuy", "admitad", "flipkart_affiliate", "quickcommerce", "involve_asia", "earnkaro", "partner",
]);

function isPartner(offer) {
  return PARTNER_SOURCES.has(offer?.source) && /^https?:/i.test(offer?.buy_url || "");
}

function storeLabel(offer) {
  const id = String(offer?.id || "");
  if (offer?.source === "ebay" || id.startsWith("ebay:")) return "eBay";
  return offer.seller_name || (isPartner(offer) ? "Partner store" : "Shoptosa");
}

export default function ComparePrices({ offers, viewingId, formatMoney, bestPrice }) {
  const rows = (offers || []).filter((o) => o?.id);
  if (!rows.length) return null;

  const cheapest = rows.find((o) => o.best) || rows[0];
  const stores = rows.length;
  const showTable = stores > 1 || rows.some((o) => isPartner(o));
  if (!showTable) return null;

  return (
    <div data-testid="compare-prices">
      <h2 className="text-[16px] font-semibold mb-1">Compare prices</h2>
      <p className="text-[12px] text-aura-muted mb-3">
        {stores} store{stores === 1 ? "" : "s"}
        {bestPrice != null && (
          <>
            {" "}· best price {formatMoney(bestPrice, cheapest?.currency || "USD")}
          </>
        )}
        . Partner checkout stays on the merchant site.
      </p>
      <div className="border border-aura-border overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_120px_140px_88px] gap-3 px-4 py-2 text-[12px] text-aura-muted bg-aura-surface border-b border-aura-border">
          <div>Store</div>
          <div>Price</div>
          <div>Availability</div>
          <div className="text-right">Buy</div>
        </div>
        <div className="divide-y divide-aura-border">
          {rows.map((o) => {
            const partner = isPartner(o);
            const viewing = o.viewing || o.id === viewingId;
            const href = partner ? o.buy_url : `/product/${encodeURIComponent(o.id)}`;
            const body = (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_140px_88px] gap-2 sm:gap-3 items-center">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-medium">{storeLabel(o)}</span>
                    {o.best && (
                      <span className="text-[10px] uppercase tracking-luxe bg-aura-accent text-white px-1.5 py-0.5">
                        Best price
                      </span>
                    )}
                    {viewing && (
                      <span className="text-[10px] uppercase tracking-luxe text-aura-muted">Viewing</span>
                    )}
                  </div>
                  <div className="text-[12px] text-aura-muted mt-0.5">
                    {partner ? "Partner · checkout off-site" : "Shoptosa seller"}
                    {o.size ? ` · ${o.size}` : ""}
                  </div>
                </div>
                <div className="text-[14px] font-semibold" data-testid="compare-price">
                  {o.price != null ? formatMoney(o.price, o.currency || "USD") : "—"}
                </div>
                <div className="text-[12px] text-aura-muted">
                  {o.availability === "OutOfStock" ? "Sold out" : "In stock"}
                  {o.condition ? ` · ${o.condition}` : ""}
                </div>
                <div className="sm:text-right">
                  <span className="inline-flex items-center justify-center gap-1 text-[13px] font-semibold px-3 py-1.5 rounded-md bg-aura-accent text-white">
                    {partner && <ExternalLink className="w-3.5 h-3.5" />}
                    {partner ? "Buy" : viewing ? "This listing" : "View"}
                  </span>
                </div>
              </div>
            );
            const className = `block px-4 py-2.5 hover:bg-aura-surface transition-colors ${
              viewing ? "bg-aura-surface" : ""
            }`;
            return partner ? (
              <a
                key={o.id}
                href={href}
                target="_blank"
                rel={o.affiliate ? "sponsored noopener" : "noopener noreferrer sponsored"}
                className={className}
                data-testid="compare-offer-partner"
              >
                {body}
              </a>
            ) : (
              <Link key={o.id} to={href} className={className} data-testid="compare-offer-local">
                {body}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
